// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { SelectPresentationProps } from "@insite/mobius/Select";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import SelectInline, { SelectInlineStyles } from "@store/Components/Toolkit/SelectInline";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { useCountriesWebsites } from "@store/Framework/Store/Data/WebsiteCountries/WebsiteCountriesHooks";
import EarthLine from "@store/Library/Icons/Globe/EarthLine";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@store/Themes/common/mixins/icon";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

const iconSize = pxToRem(14);
const iconOffset = pxToRem(0);
const menuId = "websiteMenu";

const enum fields {
    showIcon = "showIcon",
}

interface WebsiteCountriesMenuProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export interface WebsiteCountriesMenuStyles {
    countryMenuWrapper?: InjectableCss;
    websiteCountriesIcon?: IconPresentationProps;
    selectInline?: SelectInlineStyles;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const websiteCountriesMenuStyles: WebsiteCountriesMenuStyles = {
    countryMenuWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;

            &:hover {
                color: ${getColor("menu.secondaryNavigation.hover")};
                text-decoration: underline;
                transition: ${transitionHoverStyles()};
            }
        `,
    },
    websiteCountriesIcon: {
        src: EarthLine,
        color: "inherit",
        css: css`
            ${iconScalableStyles(iconSize, false)}
            margin-right: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
            position: absolute;
        `,
    },
    selectInline: {
        select: {
            cssOverrides: {
                inputSelect: css`
                    font-weight: inherit;
                    line-height: inherit;
                    cursor: pointer;
                    text-transform: inherit;
                    letter-spacing: inherit;

                    &:hover {
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
    centeringWrapper: {
        css: css`
            width: 40%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = websiteCountriesMenuStyles;

export const WebsiteCountriesMenu: FC<WebsiteCountriesMenuProps> = ({ fields }) => {
    const { countriesWebsite, currentCountryWebsite } = useCountriesWebsites();

    const handleWebsiteCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedName = event.target.value;
        const selectedCountry = countriesWebsite.find(c => c.name === selectedName);
        if (selectedCountry && selectedCountry.url) {
            let finalUrl = selectedCountry.url;

            if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
                finalUrl = `https://${finalUrl}`;
            }
            window.location.href = finalUrl;
        }
    };

    const selectInlineStyles = useMemo(() => {
        if (fields.showIcon) {
            return mergeProps<SelectInlineStyles>(styles.selectInline, {
                select: {
                    cssOverrides: {
                        inputSelect: css`
                            ${({ theme }) => {
                                const iconSpacing = theme.design.menu.secondaryNavigation.iconSpacing;
                                return `padding-left: calc(${iconSize} + ${iconSpacing} - (${iconOffset} * 2)) !important;`;
                            }}
                        `,
                    },
                },
            });
        }

        return styles.selectInline;
    }, [styles, fields.showIcon]);

    if (!countriesWebsite || countriesWebsite.length <= 1) {
        return (
            <StyledWrapper {...styles.countryMenuWrapper}>
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.countryMenuWrapper}>
            {fields.showIcon && <Icon {...styles.websiteCountriesIcon} /> /* Store Custom Code */}
            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Website")}
            </VisuallyHidden>
            <SelectInline
                uid={menuId}
                extendedStyles={selectInlineStyles}
                onChange={handleWebsiteCountryChange}
                value={currentCountryWebsite}
            >
                {countriesWebsite.map(c => (
                    <option value={c.name} key={c.id}>
                        {c.name}
                    </option>
                ))}
            </SelectInline>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: WebsiteCountriesMenu,
    definition: {
        displayName: "Website Countries Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
